import {
  faCircleNotch,
  faCloudUploadAlt,
  faFile,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type React from 'react';
import Dropzone from 'react-dropzone';
import type { FileEntry } from './useFileUploadState';

interface Props {
  acceptedTypes: string;
  maxSizeBytes: number;
  isDisabled: boolean;
  fileList: FileEntry[];
  onDrop: (files: File[]) => void;
  onDropRejected: (files: File[]) => void;
  onRemove: (key: string) => void;
  testIdPrefix?: string;
}

const FileUploadDropzone: React.FC<Props> = ({
  acceptedTypes,
  maxSizeBytes,
  isDisabled,
  fileList,
  onDrop,
  onDropRejected,
  onRemove,
  testIdPrefix = 'file-upload'
}) => (
  <>
    <Dropzone
      accept={acceptedTypes}
      maxSize={maxSizeBytes}
      disabled={isDisabled}
      onDrop={onDrop}
      onDropRejected={onDropRejected}
    >
      {({ getRootProps, getInputProps, isDragActive }) => (
        <div
          {...getRootProps({
            className: `cursor-pointer rounded border-2 border-dashed p-4 text-center transition-colors ${
              isDragActive ? 'border-primary bg-primary/5' : 'border-neutral-300'
            } ${isDisabled ? 'cursor-default opacity-60' : ''}`,
            'data-testid': `${testIdPrefix}-dropzone`
          })}
        >
          <input {...getInputProps()} data-testid={`${testIdPrefix}-input`} />
          <div className="flex justify-center items-center gap-2">
            <FontAwesomeIcon icon={faCloudUploadAlt} />
            <span className="text-neutral-500 text-sm">
              {isDragActive ? 'Drop files here' : 'Drag files here or click to select'}
            </span>
          </div>
        </div>
      )}
    </Dropzone>
    {fileList.length > 0 && (
      <div className="mt-2 flex flex-col gap-1">
        {fileList.map((f) => (
          <div
            key={f.key}
            data-testid={`${testIdPrefix}-item-${f.status}`}
            className={`flex items-center gap-2 px-2 py-1.5 rounded border text-sm ${
              f.status === 'error' ? 'border-red-400' : 'border-neutral-200'
            }`}
          >
            <FontAwesomeIcon icon={faFile} fixedWidth />
            <span className="flex-1 truncate">{f.name}</span>
            {f.status === 'uploading' && (
              <span
                className="text-neutral-400 shrink-0 inline-flex items-center gap-1"
                data-testid={`${testIdPrefix}-spinner`}
                role="status"
                aria-live="polite"
              >
                <FontAwesomeIcon icon={faCircleNotch} spin aria-hidden />
                Uploading…
              </span>
            )}
            {f.status === 'removing' && (
              <span
                className="text-neutral-400 shrink-0 inline-flex items-center gap-1"
                data-testid={`${testIdPrefix}-spinner`}
                role="status"
                aria-live="polite"
              >
                <FontAwesomeIcon icon={faCircleNotch} spin aria-hidden />
                Removing…
              </span>
            )}
            {f.status === 'done' && <span className="text-green-600 shrink-0">Uploaded ✓</span>}
            {f.status === 'error' && (
              <span className="text-red-600 shrink-0">{f.errorMessage ?? 'Error'}</span>
            )}
            {!isDisabled && f.status !== 'uploading' && f.status !== 'removing' && (
              <FontAwesomeIcon
                icon={faXmark}
                className="cursor-pointer opacity-50 shrink-0"
                onClick={() => onRemove(f.key)}
                aria-label={`Remove ${f.name}`}
              />
            )}
          </div>
        ))}
      </div>
    )}
  </>
);

export default FileUploadDropzone;
